/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */

export interface PreservationVerification {
  version?: number;
  status?: string;
  indexOk?: boolean;
  indexMatchesFiles?: boolean;
  filesOk?: boolean;
  timestamped?: boolean;
  timestampMismatch?: boolean;
  extraFiles?: string[];
  files?: Array<{path: string; ok: boolean; missing?: boolean}>;
}

export interface PreservationMe {
  isFinance?: boolean;
}

/** Chiave di lang string dello stato di un pacchetto, null se sconosciuto. */
export const preservationStatusKey = (status: string): string | null => {
  switch (status) {
    case 'TIMESTAMPED':
      return 'expensa.pres_status_timestamped';
    case 'SEALED':
      return 'expensa.pres_status_sealed';
    case 'TIMESTAMP_FAILED':
      return 'expensa.pres_status_timestamp_failed';
    default:
      return null;
  }
};

/** Tono dello StatusPill: verde con marca, giallo sigillato, rosso marca fallita. */
export const preservationTone = (status: string): string => {
  if (status === 'TIMESTAMPED') return 'APPROVED';
  if (status === 'TIMESTAMP_FAILED') return 'REJECTED';
  return 'SUBMITTED';
};

export interface VerificationSummary {
  ok: boolean;
  indexBad: boolean;
  indexMismatch: boolean;
  badFiles: string[];
  missingFiles: string[];
  extraFiles: string[];
  timestampMismatch: boolean;
}

/**
 * Riepilogo dell'esito di verify(): `ok` solo se indice, file, coerenza
 * indice/file sono a posto, non ci sono file in più né marca incoerente.
 * Un esito assente (null) non è mai "ok".
 */
export const verificationSummary = (
  v: PreservationVerification | null | undefined,
): VerificationSummary => {
  const files = v?.files ?? [];
  const missingFiles = files.filter((f) => f.missing).map((f) => f.path);
  const badFiles = files
    .filter((f) => f.ok === false && !f.missing)
    .map((f) => f.path);
  const extraFiles = v?.extraFiles ?? [];
  const indexBad = v?.indexOk !== true;
  const indexMismatch = v?.indexMatchesFiles !== true;
  const timestampMismatch = v?.timestampMismatch === true;
  const ok =
    !!v &&
    !indexBad &&
    !indexMismatch &&
    v.filesOk === true &&
    badFiles.length === 0 &&
    missingFiles.length === 0 &&
    extraFiles.length === 0 &&
    !timestampMismatch;
  return {
    ok,
    indexBad,
    indexMismatch,
    badFiles,
    missingFiles,
    extraFiles,
    timestampMismatch,
  };
};

/** "Rigenera" e "Scarica ZIP" sono solo di finance; rigenerare solo da APPROVED/REIMBURSED. */
export const preservationActions = (
  reportStatus: string,
  me: PreservationMe | null | undefined,
): {canDownloadZip: boolean; canRebuild: boolean} => {
  const finance = me?.isFinance === true;
  return {
    canDownloadZip: finance,
    canRebuild:
      finance && (reportStatus === 'APPROVED' || reportStatus === 'REIMBURSED'),
  };
};

/** 403/404 dell'elenco: la sezione non è per questo utente e si nasconde. */
export const isSectionHidden = (e: unknown): boolean => {
  const status = (e as {response?: {status?: number}})?.response?.status;
  return status === 403 || status === 404;
};

/** Riga dell'elenco: PreservationQueryService::view(), con verify() annidato. */
export interface PreservationRow {
  version: number;
  status: string;
  indexSha256?: string;
  files?: Array<{path: string; sha256?: string}>;
  timestampedAt?: string | null;
  createdAt?: string | null;
  verification?: PreservationVerification | null;
}

export interface PackageView {
  version: number;
  status: string;
  statusKey: string | null;
  tone: string;
  createdAt: string | null;
  timestampedAt: string | null;
  summary: VerificationSummary;
}

/**
 * View model di una riga: l'esito sta in `row.verification` (non nella riga,
 * né in `row.files`, che sono le voci dell'indice).
 */
export const packageView = (row: PreservationRow): PackageView => ({
  version: row.version,
  status: row.status,
  statusKey: preservationStatusKey(row.status),
  tone: preservationTone(row.status),
  createdAt: row.createdAt ?? null,
  timestampedAt: row.timestampedAt ?? null,
  summary: verificationSummary(row.verification),
});
